import {
  Box,
  Button,
  Checkbox,
  IconButton,
  ImageList,
  ImageListItem,
  LinearProgress,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Dropzone } from "../ui/dropzone";
import { useCallback, useContext, useEffect, useState } from "react";
import { createMedias, deleteMedia } from "../../api/media-api";
import CloseIcon from "@mui/icons-material/Close";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import DeleteIcon from "@mui/icons-material/Delete";
import Lightbox from "yet-another-react-lightbox";
import { Captions, Thumbnails, Zoom } from "yet-another-react-lightbox/plugins";
import { UserContext } from "../../contexts/user-context";

export const ProjectMedias = ({ project, onChanged }) => {
  const { t } = useTranslation(["project", "common"]);
  const { user } = useContext(UserContext);

  const isOwnProject = user && project?.createdById === user.id;

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);

  const handleFilesAccepted = useCallback(
    (files) => {
      const onUploadProgress = (e) => {
        setProgress(Math.round((e.loaded * 100) / e.total));
      };
      const upload = async () => {
        await createMedias(project.id, files, onUploadProgress);
        setUploading(false);
        onChanged?.();
      };
      setUploading(true);
      upload().catch(console.log);
    },
    [project.id, onChanged]
  );

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightBoxSlideIndex, setLightBoxSlideIndex] = useState(0);
  const [selectedImageIds, setSelectedImageIds] = useState([]);

  const openImage = (i) => {
    setLightBoxSlideIndex(i);
    setLightboxOpen(true);
  };

  const [shiftPressed, setShiftPressed] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      setShiftPressed(e.shiftKey);
    };
    window.addEventListener("keyup", onKey);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
    };
  }, []);

  const [lastSelectedIndex, setLastSelectedIndex] = useState(0);
  const [media, setMedia] = useState([]);
  useEffect(() => {
    if (project?.media) setMedia(project.media);
  }, [project]);

  const handleSelectChange = (i) => {
    if (shiftPressed) {
      const from = Math.min(lastSelectedIndex, i);
      const to = Math.max(lastSelectedIndex, i);
      setSelectedImageIds(media.slice(from, to + 1).map((x) => x.id));
    } else {
      const id = media[i].id;
      const index = selectedImageIds.indexOf(id);
      if (index > -1) {
        setSelectedImageIds((x) => {
          const newA = [...x];
          newA.splice(index, 1);
          return newA;
        });
      } else {
        setSelectedImageIds((x) => [...x, id]);
      }
      setLastSelectedIndex(i);
    }
  };

  const handleDeleteSelected = async () => {
    for (const selectedImageId of selectedImageIds) {
      await deleteMedia(project.id, selectedImageId);
      setSelectedImageIds((x) => {
        const index = x.indexOf(selectedImageId);
        const newA = [...x];
        newA.splice(index, 1);
        return newA;
      });
      setMedia((x) => {
        const index = x.findIndex((x) => x.id === selectedImageId);
        const newA = [...x];
        newA.splice(index, 1);
        return newA;
      });
    }
    onChanged?.();
  };

  return (
    <>
      {isOwnProject && !uploading && (
        <Dropzone sx={{ mt: 2 }} onAccepted={handleFilesAccepted} />
      )}
      {uploading && <LinearProgress variant="determinate" value={progress} />}

      {selectedImageIds.length > 0 && (
        <Paper
          sx={{
            display: "flex",
            alignItems: "center",
            mt: 2,
            top: "62px",
            position: "sticky",
            zIndex: 1000,
          }}
        >
          <IconButton
            aria-label={t("deselectAll")}
            onClick={() => setSelectedImageIds([])}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ ml: 1, flex: 1 }}>
            {t("selectedItems", { count: selectedImageIds.length })}
          </Box>
          {selectedImageIds.length !== media.length && (
            <Button
              startIcon={<SelectAllIcon />}
              onClick={() => setSelectedImageIds(media.map((x) => x.id))}
            >
              {t("selectAll")}
            </Button>
          )}
          <Button
            startIcon={<DeleteIcon />}
            color="error"
            onClick={handleDeleteSelected}
          >
            {t("deleteSelected")}
          </Button>
        </Paper>
      )}
      <ImageList cols={4} sx={{ mt: 4 }}>
        {media.map((item, i) => (
          <ImageListItem
            key={item.id}
            sx={{
              cursor: "pointer",
              "&::before,&::after": {
                backgroundImage: "linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0))",
                position: "absolute",
                top: 0,
                height: "40%",
                left: 0,
                right: 0,
                content: '""',
                zIndex: 1,
                opacity: 0,
                transition: "opacity 0.3s ease"
              },
              "&:hover::after": {
                opacity: 1,
              },
            }}
          >
            <img
              alt={item.id}
              onClick={() => openImage(i)}
              src={`${item.url}-300`}
              loading="lazy"
            />
            {isOwnProject && (
              <Box sx={{ position: "absolute", left: 0, top: 0,
                zIndex: 2 }}>
                <Checkbox
                  checked={selectedImageIds.includes(item.id)}
                  onChange={() => handleSelectChange(i)}
                />
              </Box>
            )}
          </ImageListItem>
        ))}
      </ImageList>
      <Box
        component={Lightbox}
        sx={{
          ".yarl__thumbnails_thumbnail_active": {
            borderWidth: "2px",
          },
        }}
        open={lightboxOpen}
        animation={{ swipe: 250 }}
        close={() => setLightboxOpen(false)}
        index={lightBoxSlideIndex}
        slides={media.map((x) => ({
          src: x.url,
          title: x.name,
        }))}
        carousel={{ finite: true }}
        zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }}
        plugins={[Thumbnails, Zoom, Captions]}
      />
    </>
  );
};
